import { FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { connect } from 'react-redux';
import { useFragment, graphql, RelayProp } from 'react-relay';
import {
    wireTransferFormUpdated,
    toggleBillingAddress,
} from '../../actions/checkoutPaymentActions';
import styles from './styles/CheckoutWireTransferForm.scss';

import { Checkbox } from 'dibs-elements/exports/Checkbox';
import { ControlledAddressForm } from 'dibs-address-form';
import CheckoutEoriVatForm from '../../components/forms/CheckoutEoriVatForm';

import { CheckoutWireTransferForm_viewer$key } from './__generated__/CheckoutWireTransferForm_viewer.graphql';
import { CheckoutWireTransferForm_buyer$key } from './__generated__/CheckoutWireTransferForm_buyer.graphql';

type updateType = {
    field: string;
    value: string;
};

type Address = {
    city: string;
    country: string;
    firstName: string;
    lastName: string;
    line1: string;
    line2?: string;
    state: string;
    zipCode: string;
};

type Props = {
    viewer: CheckoutWireTransferForm_viewer$key;
    buyer: CheckoutWireTransferForm_buyer$key;
    isPaymentSaving?: boolean;
    isBillingSameAsShipping: boolean;
    wireShowFailedSubmissionError: boolean;
    newBillingForm?: Address;
    shippingCountry: string;
    onToggleBillingAddress: (isBillingSameAsShipping: boolean) => void;
    onWireTransferFormUpdated: (updatedField: updateType) => void;
    relay?: RelayProp;
};

const CheckoutWireTransferForm: FC<Props> = ({
    isPaymentSaving = false,
    isBillingSameAsShipping,
    wireShowFailedSubmissionError,
    viewer: viewerRef,
    buyer: buyerRef,
    newBillingForm = {} as Address,
    relay = {},
    shippingCountry,
    onToggleBillingAddress,
    onWireTransferFormUpdated,
}) => {
    const buyer = useFragment(
        graphql`
            fragment CheckoutWireTransferForm_buyer on User {
                ...CheckoutEoriVatForm_buyer
                isTrade: isVerifiedTrade
            }
        `,
        buyerRef
    );
    const viewer = useFragment(
        graphql`
            fragment CheckoutWireTransferForm_viewer on Viewer {
                ...ControlledAddressForm_viewer
            }
        `,
        viewerRef
    );

    const onToggle = (checked: boolean): void => onToggleBillingAddress(checked);

    const onUpdate = ({ field, value }: updateType): void =>
        onWireTransferFormUpdated({ field, value });

    const isTrade = !!buyer?.isTrade;
    const showAddressForm = !isBillingSameAsShipping;
    const foreignBillingCountry = isBillingSameAsShipping
        ? !!shippingCountry && shippingCountry !== 'US'
        : !!newBillingForm?.country && newBillingForm?.country !== 'US';
    return (
        <div>
            <Checkbox
                name="isBillingSameAsShipping"
                label={
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferForm.sameAsShipping"
                        defaultMessage="Billing Address is the Same as Shipping Address"
                    />
                }
                checked={isBillingSameAsShipping}
                disabled={isPaymentSaving}
                wrapperClass={styles.checkbox}
                onChange={onToggle}
                dataTn="same-as-shipping-address-checkbox"
            />
            {showAddressForm && (
                <ControlledAddressForm
                    relay={relay}
                    viewer={viewer}
                    address={newBillingForm}
                    hideCompany
                    hidePhoneNumber
                    hideSubmit
                    validations={{ allowPOBox: true }}
                    disableForm={isPaymentSaving}
                    onUpdate={onUpdate}
                    showAllErrors={wireShowFailedSubmissionError}
                />
            )}
            {isTrade && foreignBillingCountry && (
                <CheckoutEoriVatForm buyer={buyer} hideEori={false} />
            )}
        </div>
    );
};

type connectPaymentType = {
    newBillingForm: Address;
    isBillingSameAsShipping: boolean;
    isPaymentSaving: boolean;
    wireShowFailedSubmissionError: boolean;
};

type connectShippingType = {
    defaultCountry: string;
};

type connectType = {
    payment: connectPaymentType;
    shipping: connectShippingType;
};

const ConnectedCheckoutWireTransferForm = connect(
    ({ payment, shipping }: connectType) => {
        return {
            isPaymentSaving: payment.isPaymentSaving,
            newBillingForm: payment.newBillingForm,
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            wireShowFailedSubmissionError: payment.wireShowFailedSubmissionError,
            shippingCountry: shipping.defaultCountry,
        };
    },
    {
        onWireTransferFormUpdated: wireTransferFormUpdated,
        onToggleBillingAddress: toggleBillingAddress,
    }
)(CheckoutWireTransferForm);

export default ConnectedCheckoutWireTransferForm;
